import styles from './index.module.css'

import { LuArrowDown } from '@react-icons/all-files/lu/LuArrowDown'
import { LuArrowUp } from '@react-icons/all-files/lu/LuArrowUp'
import { LuArrowUpDown } from '@react-icons/all-files/lu/LuArrowUpDown'
import { LuChevronLeft } from '@react-icons/all-files/lu/LuChevronLeft'
import { LuChevronRight } from '@react-icons/all-files/lu/LuChevronRight'
import { LuEye } from '@react-icons/all-files/lu/LuEye'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { LICENSE_REQUEST } from '@disco/constants/FeatureTypes'
import type { LicenseRequestSubmissionTrack } from '@disco/state-management/api/license-requests'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getClientLibrary } from '@disco/state-management/selectors/client-libraries'
import { getConstanceSettings } from '@disco/state-management/selectors/user'
import { getInitials } from '@disco/utils/string-utils'

import type { ClientLibraryId } from 'types/client-library'
import type { State } from 'types/redux'

import DocumentHeadTags from 'components/document-head-tags'
import {
  Avatar,
  Box,
  Flex,
  IconButton,
  Separator,
  Switch,
  Table,
  Text,
} from 'components/ds'

import { useSelector } from 'hooks/use-redux'

import { useDebouncedLibraryToggle } from '../use-debounced-library-toggle'
import { formatDate } from './format-date'
import { SubmissionDetailPanel } from './submission-detail-panel'
import { useLicenseRequestSubmissions } from './use-license-request-submissions'

type SortColumn = 'id' | 'created'

function getSortIcon(column: SortColumn, ordering: string) {
  const isAsc = ordering === column
  const isDesc = ordering === `-${column}`
  if (!isAsc && !isDesc) return <LuArrowUpDown size={12} />
  return isAsc ? <LuArrowUp size={12} /> : <LuArrowDown size={12} />
}

function TrackRow({ track }: { track: LicenseRequestSubmissionTrack }) {
  return (
    <Flex gap="2" align="center">
      {track.artwork?.artwork_sizes['160x160'] ? (
        <img
          src={track.artwork.artwork_sizes['160x160']}
          alt=""
          className={styles.trackArtwork}
        />
      ) : (
        <div className={styles.trackArtworkPlaceholder} />
      )}
      <Flex direction="column" gap="1">
        <Text size="2" weight="medium">
          {track.name}
        </Text>
        <Text size="2" color="gray">
          {track.artist}
        </Text>
      </Flex>
    </Flex>
  )
}

function TrackList({ tracks }: { tracks: LicenseRequestSubmissionTrack[] }) {
  const [expanded, setExpanded] = useState(false)

  if (tracks.length === 0) {
    return (
      <Text size="2" color="gray">
        —
      </Text>
    )
  }

  const extraCount = tracks.length - 1

  return (
    <Flex direction="column" gap="2" className={styles.trackListContent}>
      <Flex align="center" justify="between" gap="2">
        <TrackRow track={tracks[0]!} />
        {extraCount > 0 && (
          <button
            type="button"
            className={styles.trackMoreButton}
            onClick={() => setExpanded(e => !e)}
          >
            +{extraCount}
          </button>
        )}
      </Flex>
      {expanded &&
        tracks.slice(1).map(track => <TrackRow key={track.id} track={track} />)}
    </Flex>
  )
}

const LibrariesLibraryLicenseRequests = () => {
  const params = useParams<{ libraryId: string }>()
  const libraryId: ClientLibraryId = parseInt(params.libraryId!, 10)
  const licenseRequestEnabled = useFeatureEnabled(LICENSE_REQUEST)

  const constance = useSelector(getConstanceSettings)
  const licenseRequestListingEnabled = constance.LICENSE_REQUESTS_LISTING

  const library = useSelector((state: State) =>
    getClientLibrary(state, libraryId),
  )

  const [ordering, setOrdering] = useState('-created')
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  const {
    submissions,
    total,
    start,
    end,
    hasNext,
    hasPrev,
    goNext,
    goPrev,
    isLoading,
  } = useLicenseRequestSubmissions(libraryId, ordering)

  const toggleOrdering = (column: SortColumn) => {
    if (ordering === `-${column}`) {
      setOrdering(column)
    } else {
      setOrdering(`-${column}`)
    }
  }

  const [enableLicenseRequest, toggleEnableLicenseRequest] =
    useDebouncedLibraryToggle(
      libraryId,
      'enable_license_request',
      Boolean(library?.enable_license_request),
    )

  const handleToggle = (checked: boolean) => {
    mixpanel.sendPureEvent('License request settings changed', {
      enable: checked,
    })
    toggleEnableLicenseRequest(checked)
  }

  if (!(licenseRequestListingEnabled && licenseRequestEnabled)) {
    return <Text>License requests feature currently not enabled</Text>
  }
  const selectedSubmission =
    selectedIndex != null ? submissions[selectedIndex] ?? null : null

  return (
    <>
      {library && !library['loading'] && (
        <DocumentHeadTags
          title={`${library['name']} | License requests | DISCO`}
        />
      )}

      <Box p="4" mb="5" className={styles.tableCard}>
        <h1 className="Heading Heading--500">License request form</h1>
        <Text size="2" color="gray" as="p" mb="3" mt="2">
          Enable a license request form to let visitors submit licensing
          inquiries directly from your Catalog. This starts an email thread
          between the requester and your Catalog&apos;s notification recipients.
        </Text>
        <Flex gap="2" align="center">
          <Switch
            disabled={Boolean(library?.loading)}
            checked={enableLicenseRequest}
            onCheckedChange={handleToggle}
          />
          <Text size="2">Enable license request form</Text>
        </Flex>
      </Box>

      <div className={styles.tableCard}>
        <div className={styles.toolbar}>
          {/* Placeholder for future date-range filter and action buttons */}
          <div />
          <Flex gap="2" align="center">
            {total > 0 && (
              <Text size="2" color="gray">
                {start} – {end} of {total}
              </Text>
            )}
            <Flex gap="1">
              <IconButton
                variant="soft"
                size="1"
                disabled={!hasPrev || isLoading}
                onClick={goPrev}
                aria-label="Previous page"
              >
                <LuChevronLeft size={14} />
              </IconButton>
              <IconButton
                variant="soft"
                size="1"
                disabled={!hasNext || isLoading}
                onClick={goNext}
                aria-label="Next page"
              >
                <LuChevronRight size={14} />
              </IconButton>
            </Flex>
          </Flex>
        </div>

        <Separator size="4" />
        <Table.Root variant="ghost">
          <Table.Header>
            <Table.Row className={styles.tableHeader}>
              <Table.ColumnHeaderCell>
                <button
                  type="button"
                  className={styles.sortableHeader}
                  onClick={() => toggleOrdering('id')}
                >
                  Request ID
                  {getSortIcon('id', ordering)}
                </button>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                <button
                  type="button"
                  className={styles.sortableHeader}
                  onClick={() => toggleOrdering('created')}
                >
                  Submission Date
                  {getSortIcon('created', ordering)}
                </button>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Track Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>View</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading && submissions.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={6}>
                  <Text size="2" color="gray">
                    Loading…
                  </Text>
                </Table.Cell>
              </Table.Row>
            )}
            {!isLoading && submissions.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={6}>
                  <Text size="2" color="gray">
                    No submissions yet.
                  </Text>
                </Table.Cell>
              </Table.Row>
            )}
            {submissions.map((submission, rowIndex) => {
              const initials = getInitials(submission.answers.user_name ?? '')

              return (
                <Table.Row key={submission.id}>
                  <Table.Cell>
                    <Text size="2" color="gray">
                      {submission.id}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Box className={styles.nameContent}>
                      <Flex gap="2" align="center">
                        <Avatar
                          size="1"
                          fallback={initials || '?'}
                          radius="full"
                          className={styles.avatar}
                        />
                        <Text size="2" truncate>
                          {submission.answers.user_name}
                        </Text>
                      </Flex>
                    </Box>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2" color="gray">
                      {submission.answers.user_email}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2" color="gray">
                      {formatDate(submission.created)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <TrackList tracks={submission.tracks} />
                  </Table.Cell>
                  <Table.Cell>
                    <Flex align="start" pt="3">
                      <IconButton
                        variant="ghost"
                        size="1"
                        onClick={() => setSelectedIndex(rowIndex)}
                        aria-label="View submission"
                      >
                        <LuEye size={16} />
                      </IconButton>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Root>
      </div>

      <SubmissionDetailPanel
        open={selectedIndex != null}
        submission={selectedSubmission}
        onClose={() => setSelectedIndex(null)}
        onPrev={() => setSelectedIndex(i => (i != null ? i - 1 : null))}
        onNext={() => setSelectedIndex(i => (i != null ? i + 1 : null))}
        prevDisabled={selectedIndex === 0}
        nextDisabled={selectedIndex === submissions.length - 1}
      />
    </>
  )
}

export default LibrariesLibraryLicenseRequests
